import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useContent } from '../../hooks/useContent';

const fallback = [
  { name: 'Priya Sharma', location: 'Mumbai', role: 'Homeowner', text: 'After Dr. Ahirrao\'s Vastu consultation, our home feels completely transformed. The positive energy is palpable and our family\'s health and finances have improved remarkably.', rating: 5 },
  { name: 'Rajesh Mehta', location: 'Pune', role: 'Business Owner', text: 'My business was struggling for years. Within 3 months of implementing Dr. Ahirrao\'s Vastu remedies, our revenue doubled. His scientific approach is truly remarkable.', rating: 5 },
  { name: 'Sunita Patel', location: 'Thane', role: 'Architect', text: 'As an architect, I was skeptical. But Dr. Ahirrao\'s 12-type audit system is methodical and scientific. I now incorporate Vastu principles in all my projects.', rating: 5 },
  { name: 'Vikram Joshi', location: 'Nashik', role: 'Factory Owner', text: 'Our industrial unit had constant issues. Post Vastu correction, worker productivity increased by 40% and accidents reduced to zero. Highly recommend!', rating: 5 },
];

export default function Testimonials() {
  const { content } = useContent();
  const t = content?.testimonials;
  const testimonials = t?.list ?? fallback;
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent(c => (c + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (i: number) => {
    setDirection(i > current ? 1 : -1);
    setCurrent(i);
  };

  return (
    <section ref={sectionRef} style={{ padding: '100px 0', background: 'linear-gradient(180deg, #1A0F2E 0%, #0D0D1F 100%)', overflow: 'hidden', position: 'relative' }}>
      {/* Floating decorative symbols */}
      {['ॐ', '✦', '☸'].map((sym, i) => (
        <motion.span key={i}
          animate={{ y: [0, -20, 0], opacity: [0.05, 0.12, 0.05] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay: i * 1.2 }}
          style={{
            position: 'absolute', fontSize: 60, color: '#E8871A',
            left: i === 0 ? '5%' : i === 1 ? '90%' : '50%',
            top: i === 0 ? '20%' : i === 1 ? '60%' : '80%',
            fontFamily: 'serif', pointerEvents: 'none', userSelect: 'none'
          }}
        >{sym}</motion.span>
      ))}

      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 60 }}
        >
          <p className="section-eyebrow">{t?.eyebrow ?? 'Client Stories'}</p>
          <h2 className="section-title">{t?.title ?? 'Transformations That Inspire'}</h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 60 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ height: 2, background: 'linear-gradient(90deg, #E8871A, transparent)', margin: '20px auto' }}
          />
        </motion.div>

        <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative', minHeight: 280 }}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current}
              custom={direction}
              initial={{ opacity: 0, x: direction * 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -80 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              style={{
                background: 'rgba(232,135,26,0.04)',
                border: '1px solid rgba(232,135,26,0.15)',
                borderRadius: 16, padding: '48px',
                textAlign: 'center', position: 'relative'
              }}
            >
              {/* Quote mark */}
              <motion.p
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{ fontSize: 60, color: '#E8871A', lineHeight: 1, marginBottom: 16 }}
              >"</motion.p>

              <p style={{ fontSize: 17, lineHeight: 1.9, opacity: 0.85, marginBottom: 32, fontStyle: 'italic' }}>
                {testimonials[current].text}
              </p>

              {/* Stars */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 16 }}>
                {Array(testimonials[current].rating).fill(0).map((_, i) => (
                  <motion.span key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    style={{ color: '#E8871A', fontSize: 18 }}
                  >★</motion.span>
                ))}
              </div>

              <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, color: '#E8871A' }}>
                {testimonials[current].name}
              </p>
              <p style={{ fontSize: 13, opacity: 0.5, marginTop: 4 }}>
                {testimonials[current].role} · {testimonials[current].location}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Navigation dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 32 }}>
            {testimonials.map((_: any, i: number) => (
              <motion.button key={i} onClick={() => goTo(i)}
                whileHover={{ scale: 1.3 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  width: i === current ? 28 : 8, height: 8,
                  borderRadius: 4, border: 'none', cursor: 'pointer',
                  background: i === current ? '#E8871A' : 'rgba(232,135,26,0.3)',
                  transition: 'all 0.3s ease', padding: 0
                }}
              />
            ))}
          </div>

          {/* Arrow buttons */}
          {[-1, 1].map((dir, i) => (
            <motion.button key={i}
              whileHover={{ scale: 1.1, background: 'rgba(232,135,26,0.2)' }}
              whileTap={{ scale: 0.9 }}
              onClick={() => { setDirection(dir); setCurrent(c => (c + dir + testimonials.length) % testimonials.length); }}
              style={{
                position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                ...(i === 0 ? { left: -48 } : { right: -48 }),
                width: 36, height: 36, borderRadius: '50%',
                border: '1px solid rgba(232,135,26,0.3)',
                background: 'transparent', color: '#E8871A',
                cursor: 'pointer', fontSize: 16, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s'
              }}
            >
              {i === 0 ? '←' : '→'}
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
